import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiOutlineX } from 'react-icons/hi';
import { lockScroll, unlockScroll } from '../utils/scrollLock';

const LegalModal = ({ isOpen, onClose, title, updated, sections }) => {
  const dialogRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    lockScroll();
    return () => unlockScroll();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onWheel = (e) => {
      const dialog = dialogRef.current;
      const scrollEl = scrollRef.current;
      if (!dialog || !scrollEl) return;

      if (!dialog.contains(e.target)) {
        e.preventDefault();
        return;
      }

      e.preventDefault();
      scrollEl.scrollTop += e.deltaY;
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, [isOpen]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/75 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden
          />
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="legal-modal-title"
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-2xl max-h-[min(88vh,720px)] bg-lumora-dark-surface border border-lumora-cream/10 rounded-3xl shadow-hero flex flex-col overflow-hidden"
          >
            <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-lumora-cream/10 flex-shrink-0">
              <div className="min-w-0 pr-2">
                <h2 id="legal-modal-title" className="font-serif text-xl sm:text-2xl text-lumora-cream">
                  {title}
                </h2>
                {updated && (
                  <p className="text-xs text-lumora-muted mt-1">Last updated: {updated}</p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-10 h-10 rounded-full border border-lumora-cream/15 flex items-center justify-center text-lumora-cream hover:border-lumora-gold/40 hover:text-lumora-gold transition-colors flex-shrink-0"
                aria-label="Close"
              >
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>

            <div
              ref={scrollRef}
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-6 space-y-6"
            >
              {sections.map((section) => (
                <div key={section.heading}>
                  <h3 className="font-serif text-base text-lumora-gold mb-2">{section.heading}</h3>
                  <p className="text-sm text-lumora-muted leading-relaxed">{section.body}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default LegalModal;
